import { getDefaultStore, useAtom } from 'jotai';
import { getUrlParam, handleMergeAbinfo } from '.';
import * as native from '@/utils/native';
import { QWebChannel } from '@/assets/js/qwebchannel';
import { fetchAuditVersionStatus } from '@/services/subscription';
import {
  auditVersionStatusAtom,
  curTabAtom,
  inviterUidAtom,
  isInitAtom,
  isViewNewUiAtom,
  rebateCodeAtom,
  subEntranceBizCodeAtom,
  userInfoAtom,
  vipInfoAtom,
  visibleExchangeAtom,
} from '@/store';
import { TrackData } from '@/page/subscription';
import {
  getGid,
  getIncomeSource,
  getTheme,
  getClientId,
  setToken,
  getTokenAndSet,
  closeVipSubscribeDialog,
} from './vip';
import {
  fetchUserToken,
  getInitData,
  getRebateCode,
  getSubPromoteRebateCode,
} from '@/services/user';
import { getAccountLoginUrl, getUserBatchSize, getUserInfo } from './user';
import { isTeamSubscription, SubscribeType, subscribeType } from '@/constants';
import { TabType } from '@/constants/subscription';
import initMtstat from './mtstat';

const store = getDefaultStore();

/**
 * 获取服务端下发的是否需要勾选协议交互
 */
export const getAuditVersionStatus = async () => {
  const channel = getUrlParam('channel') || '';
  const version = await native.getVersion();
  const res = await fetchAuditVersionStatus({
    channel,
    version,
  });
  store.set(auditVersionStatusAtom, !!res?.data?.data?.status);
};

export const initQt = async () => {
  if (window.qt) {
    await new Promise<void>((rsolve) => {
      new QWebChannel(window.qt.webChannelTransport, (channel: any) => {
        window.qtObject = channel.objects.content;
        rsolve();
      });
    });
  }
};

/**
 * 埋点
 */
export const track = async (data: TrackData) => {
  // web端
  if (getIncomeSource() === 'MTXX-WEB') {
    if (!data.params) {
      data.params = {};
    }
    data.params.inviter = store.get(inviterUidAtom) || '';
    window._stat_track?.(data.event_id, data.params);
    return;
  }

  // PC端
  if (data.params) {
    const userInfo = store.get(userInfoAtom);
    const vipInfo = store.get(vipInfoAtom);
    const appVersion = await native.getVersion();
    // PC公共上报字段
    data.params.uid = `${userInfo?.id || ''}`;
    data.params.renew = (vipInfo && vipInfo.vip_end_time && !(vipInfo.vip_end_time < 0)) || false;
    data.params.channel = getUrlParam('channel') || '';
    data.params.entrance = getUrlParam('entrance') || '';

    data.params.app_version = appVersion || '';
    data.params.ab_info = decodeURIComponent(getUrlParam('ab_info') || '');

    // 透传权益相关
    const source = getUrlParam('source');
    const first_source = getUrlParam('first_source');
    const second_source = getUrlParam('second_source');
    const third_source = getUrlParam('third_source');
    const entrance_type = getUrlParam('entrance_type');
    const ID = getUrlParam('ID');
    if (source) data.params.source = source;
    if (first_source) data.params.first_source = first_source;
    if (second_source) data.params.second_source = second_source;
    if (third_source) data.params.third_source = third_source;
    if (entrance_type) data.params.entrance_type = entrance_type;
    if (ID) data.params.ID = ID;
  }

  native?.mtTrack(data);
};

const initCurTabValue = () => {
  const vipInfo = store.get(vipInfoAtom);
  const moduleId = getUrlParam('module_id');
  const source = getUrlParam('source');

  const isOnlyShowScanUI = getUrlParam('isOnlyShowScanUI');
  if (isOnlyShowScanUI === '1') {
    // 从高级会员需求开始，废弃了URL透传default_tab来控制引导购买类型，改成全部直接在订阅弹窗内部自己控制
    // 只有在打开支付二维码弹窗时（isOnlyShowScanUI=1），才会接收使用default_tab
    const initDefaultTab = getUrlParam('default_tab');
    if (initDefaultTab) {
      store.set(curTabAtom, initDefaultTab as TabType);
      return;
    }
  }

  // 美豆高消耗项目[module_id, source]
  const hightConsumptionModules: string[][] = [
    ['roboneo_web', 'agent'], // agent编辑器
    ['product_kit_web', 'product_kit_web'], // AI商品套图
    ['product_kit_web', 'try_on'], // AI穿戴套图
    ['product_kit_web', 'A+_content'], // A+详情页
    ['product_shoot_web', 'product_shoot'], // AI商品图
    ['ai_commercial_web', 'aimodel'], // AI模特
    ['ai_commercial_web', 'ai_try_on_shoes'], // AI试鞋
    ['ailogo', 'ailogo'], // AI logo
    ['ai_commercial_web', 'personbg'], // 人像背景
  ];

  let defaultTab: any = '';

  switch (subscribeType) {
    case SubscribeType.SUBSCRIPTION:
      // 会员场景
      if (vipInfo?.is_vip) {
        // 会员（高级会员/基础会员），默认引导购买高级会员
        defaultTab =
          getIncomeSource() !== 'MTXX-WEB' || isTeamSubscription ? TabType.SUB : TabType.PRO_SUB;
      } else {
        // 非会员高消耗场景，默认引导购买高级会员
        // 非会员非高消耗场景，默认引导购买基础会员
        defaultTab =
          getIncomeSource() === 'MTXX-WEB' &&
          !isTeamSubscription &&
          hightConsumptionModules.some(([m, s]) => m === moduleId && s === source)
            ? TabType.PRO_SUB
            : TabType.SUB;
      }

      break;
    case SubscribeType.MEIDOU:
      // 美豆场景
      if (vipInfo?.is_vip) {
        defaultTab = TabType.MEIDOU;
      } else {
        // 非会员高消耗场景，默认引导购买高级会员
        // 非会员非高消耗场景，默认引导购买基础会员
        defaultTab =
          getIncomeSource() === 'MTXX-WEB' &&
          !isTeamSubscription &&
          hightConsumptionModules.some(([m, s]) => m === moduleId && s === source)
            ? TabType.PRO_SUB
            : TabType.SUB;
      }
      break;
    case SubscribeType.CONSUMABLE:
      // 抠图场景
      if (vipInfo?.is_vip) {
        defaultTab = TabType.CUTOUT;
      } else {
        defaultTab = TabType.SUB;
      }
      break;
    case SubscribeType.LOGO_UNLIMITED:
      // logo无限场景
      defaultTab = TabType.UNLIMITED;
      break;
    default:
  }

  if (defaultTab) {
    store.set(curTabAtom, defaultTab);
  }
};

/**
 * 页面前置初始化逻辑
 */
export const initPage = async () => {
  const isMtxxWeb = getIncomeSource() === 'MTXX-WEB';
  // URL没携带type类型则默认展示vip类型
  const typeP = (getUrlParam('type') as string) || 'vip';

  if (!isMtxxWeb) {
    await initQt();

    if (getUrlParam('client_referer') === 'sub') {
      // PC端订阅登录后回跳到该页面需要触发pc重新发页面进入事件
      track({
        event_id: 'vip_page_enter_new',
        event_name: '页面进入',
        params: {},
      });
    }
  }

  // 获取并缓存gid
  const gid = await getGid();

  // 主题色
  const theme = await getTheme();
  document.documentElement.className = theme;

  if (!isMtxxWeb) {
    const token = getUrlParam('token');
    const code = getUrlParam('code');
    const op_type = getUrlParam('op_type');

    // 3-退出登录, 7-绑定成功, 16-取消绑定
    if (op_type === '3' || op_type === '7' || op_type === '16') {
      if (op_type === '3') await native.logout();
      native.closeVipSubscribeDialog();
      native.closeAccountLoginDialog();
      return;
    }

    console.log('code==========', code);
    if (code || token) {
      // NOTE 登录回跳后cookie会自动种植 __mt_web_access_token__ ，调用fetchUserToken接口时需要cookie上带有 __mt_web_access_token__
      try {
        const tokenRes = await fetchUserToken({
          code,
          token,
          client_id: getClientId(),
        });
        console.log('code to token success==========');
        if (tokenRes?.data?.data?.access_token) {
          setToken(tokenRes.data.data);

          // pc订阅中，支持pc端登录操作。直接关闭弹窗
          if (getUrlParam('state') !== 'sub' && getUrlParam('client_referer') !== 'sub') {
            native.closeVipSubscribeDialog();
            native.closeAccountLoginDialog();
            // setTimeout(() => {
            //   // 不知道干嘛用，从N多年前仓库代码搬迁过来，不敢随意删，就加延时调用（如果直接调用又影响新浏览器执行上面协议）
            //   window.close();
            // }, 300);
            return;
          }
        }
      } catch (e) {
        console.log('code to token fail==========', e);
      }
    } else {
      //  从PC端取token并缓存到本地
      await getTokenAndSet();
      // 子组件异步调用，要先等token拿到之后在加载子组件
      getAccountLoginUrl(typeP);
    }
    // 桌面端，获取审核期间开关状态
    getAuditVersionStatus();
  } else {
    // web端
    // const is_debugger = getUrlParam('debugger'); // debugger为1，代表本地调试模式
    // if (!+is_debugger) {
    //   try {
    //     const res: any = await getAccesTokenByCookie();
    //     const access_token = res?.data?.response?.access_token;
    //     // web订阅要求登录态下才能打开
    //     if (access_token) {
    //       window.localStorage.setItem('access_token', access_token);
    //     } else {
    //       // 未登录，通知上层需要登录
    //       closeVipSubscribeDialog({
    //         needLogin: true,
    //       });
    //     }
    //   } catch (e) {
    //     // 未登录，通知上层需要登录
    //     closeVipSubscribeDialog({
    //       needLogin: true,
    //     });
    //   }
    // }
  }

  const curTab = store.get(curTabAtom);

  const reqList: any = [getUserInfo(), getInitData()];

  // 获取团队版/个人版下的分佣码
  isTeamSubscription ? reqList.push(getRebateCode()) : reqList.push(getSubPromoteRebateCode());

  const res = await Promise.all(reqList);
  const { access_token, uid } = res?.[0];

  if (res?.[1]) {
    const entranceCode = res?.[1]?.data?.data?.web_sub_entrance;
    entranceCode && store.set(subEntranceBizCodeAtom, entranceCode);
  }

  if (res?.[2]) {
    store.set(rebateCodeAtom, res[2].data?.data?.rebate_code || '');
    store.set(inviterUidAtom, res[2].data?.data?.inviter_uid || '');
  }

  if (access_token) {
    window.localStorage.setItem('access_token', access_token);
  } else {
    // web侧
    if (isMtxxWeb) {
      // 未登录，通知上层需要登录
      closeVipSubscribeDialog({
        needLogin: true,
      });
    }
  }

  // 初始化curTab的默认值
  initCurTabValue();

  // 初始化埋点
  await initMtstat(uid);

  store.set(isInitAtom, true);

  if (typeP === 'svip') {
    getUserBatchSize();
  }

  // 如果携带user_promote_code邀请码字段，则自动打开兑换码页面
  if (getUrlParam('user_promote_code')) {
    store.set(visibleExchangeAtom, true);
  }
};
